import { C9Logger } from 'c9-js-log-client';
import { BaseClient, BaseClientImpl } from './base.client';

/**
 * KeepSessionAlive - This will add event listeners to the document to detect user activity.
 *
 * Every 5 minutes the pingRefreshEndpoint method will be called, and if activity has been detected
 * it will ping the refresh endpoint so that the JWT's timestamp is updated in DynamoDB so that the JWT
 * does not expire.
 *
 * If for some reason the ping fail, it will try again
 * after one minute.
 *
 * @category Keep Session Alive Client
 *
 */
export interface KeepSessionAlive extends BaseClient {
    authToken: string | undefined;
    activityDetected: boolean;
    invalidCredential: boolean;
    ping: () => Promise<void>;
}

/**
 * @internal
 */
export class KeepSessionAliveImpl extends BaseClientImpl implements KeepSessionAlive {
    public static APP_NAME = 'KeepSessionAliveClient';
    /**
     * interval in milliseconds
     *
     * 300000 ms = 5 minutes
     */
    private static REFRESH_INTERVAL = 300000;

    private intervalId?: number;
    private static readonly RETRY_INTERVAL = 60000; // 1 minute

    private readonly keepAliveEndpoint: string | undefined;

    private _authToken: string | undefined;
    private _activityDetected: boolean;
    private _invalidCredential: boolean;

    private retryAttempts = 0;

    constructor(logger: C9Logger, keepAliveEndpoint?: string, authToken?: string) {
        super(logger);

        this.authToken = authToken;
        this.keepAliveEndpoint = keepAliveEndpoint;

        this._activityDetected = false;
        this._invalidCredential = false;

        if (keepAliveEndpoint) {
            this.setup();
        } else {
            this.logger.warn(
                `cannot run setup because "keep alive" endpoint is not defined!`,
                KeepSessionAliveImpl.APP_NAME
            );
        }
    }

    public get authToken(): string | undefined {
        return this._authToken;
    }

    public set authToken(value: string | undefined) {
        this._authToken = value;
    }

    public get activityDetected(): boolean {
        return this._activityDetected;
    }

    public set activityDetected(value: boolean) {
        this._activityDetected = value;
    }

    public get invalidCredential(): boolean {
        return this._invalidCredential;
    }

    public set invalidCredential(value: boolean) {
        this._invalidCredential = value;
    }

    public async ping(): Promise<void> {
        await this.pingKeepAliveEndpoint();
    }

    private clearTimer() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = undefined;
        }
    }

    private startTimer() {
        this.clearTimer();
        this.intervalId = window.setInterval(
            this.pingKeepAliveEndpoint.bind(this),
            KeepSessionAliveImpl.REFRESH_INTERVAL
        );
    }

    private startRetryTimer() {
        this.clearTimer();
        this.intervalId = window.setInterval(
            this.pingKeepAliveEndpoint.bind(this),
            KeepSessionAliveImpl.RETRY_INTERVAL
        );
    }

    private async pingKeepAliveEndpoint(): Promise<void> {
        try {
            // Ensure we reset activity flag on every ping attempt
            this.activityDetected = false;

            if (!this.authToken) {
                this.clearTimer();
                this.logger.warn(`authToken not found, stopping "keep alive" check`, KeepSessionAliveImpl.APP_NAME);
                return;
            }

            if (!this.keepAliveEndpoint) {
                this.clearTimer();
                this.logger.warn(
                    `cannot run setup because "keep alive" endpoint is not defined!`,
                    KeepSessionAliveImpl.APP_NAME
                );
                return;
            }

            const fetchInit: RequestInit = {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${this.authToken}`,
                    'x-top-appname': 'TopContext'
                }
            };

            const response: Response = await fetch(this.keepAliveEndpoint!, fetchInit);

            // Stop on invalid credential statuses
            if (response.status === 401 || response.status === 403) {
                this.invalidCredential = true;
                this.clearTimer();
                this.logger.info(`Credentials are invalid, stopping "keep alive" check`, KeepSessionAliveImpl.APP_NAME);
                return;
            }

            // Handle non-200 responses as failures (excluding 401/403)
            if (response.status !== 200) {
                this.retryAttempts += 1;

                // First failure -> switch to 1 minute interval and log
                if (this.retryAttempts === 1) {
                    this.startRetryTimer();
                    this.logger.info(
                        `Received status code ${response.status}. Will now ping "keep alive" endpoint at a 1 minute interval instead`,
                        KeepSessionAliveImpl.APP_NAME
                    );
                    return;
                }

                // Second failure -> keep retrying (1 min)
                if (this.retryAttempts === 2) {
                    // keep the 1-minute interval running
                    return;
                }

                // Third failure -> stop and log completion of max checks
                if (this.retryAttempts >= 3) {
                    this.clearTimer();
                    this.retryAttempts = 0;
                    this.logger.info(
                        `"keep alive" check has completed the maximum amount of checks (2 times)`,
                        KeepSessionAliveImpl.APP_NAME
                    );
                    return;
                }
            }

            // Success -> reset retries and keep normal interval
            this.retryAttempts = 0;
            // On success we keep current interval; if we were on retry interval,
            // you can optionally restore the normal 5-min interval:
            // this.startTimer();
        } catch (e) {
            // Network or unexpected error -> treat as a failure and switch to 1-minute on first error
            this.retryAttempts += 1;

            if (this.retryAttempts === 1) {
                this.startRetryTimer();
                this.logger.info(
                    `Received status code 500. Will now ping "keep alive" endpoint at a 1 minute interval instead`,
                    KeepSessionAliveImpl.APP_NAME
                );
                return;
            }

            if (this.retryAttempts >= 3) {
                this.clearTimer();
                this.retryAttempts = 0;
                this.logger.info(
                    `"keep alive" check has completed the maximum amount of checks (2 times)`,
                    KeepSessionAliveImpl.APP_NAME
                );
                return;
            }
        }
    }

    private detectActivity(): void {
        this.activityDetected = true;
    }

    private setup() {
        document.addEventListener('mousemove', this.detectActivity.bind(this), false);
        document.addEventListener('mousedown', this.detectActivity.bind(this), false);
        document.addEventListener('keypress', this.detectActivity.bind(this), false);
        document.addEventListener('touchmove', this.detectActivity.bind(this), false);
        this.startTimer();
    }
}
